import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getAllEvents } from "@/lib/storage";
import {
  todayISO,
  toISODate,
  parseISODate,
  pad2,
} from "@/lib/time";
import { MONTH_VISIBLE_CATEGORIES } from "@/lib/monthCategories";
import { getCategory } from "@/lib/categories";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";

// View modes:
//   "rolling30" — 30 day rolling window starting today (default)
//   "month"     — calendar month grid (Mon-Sun)

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function addDays(d, n) {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function weekdayIndexMonFirst(d) {
  // JS getDay(): Sun=0..Sat=6. Convert to Mon=0..Sun=6.
  return (d.getDay() + 6) % 7;
}

export default function MonthOverview() {
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialMode = searchParams.get("mode") === "month" ? "month" : "rolling30";
  const initialAnchor = searchParams.get("anchor")
    ? parseISODate(searchParams.get("anchor"))
    : new Date();

  const [mode, setMode] = useState(initialMode);
  const [anchor, setAnchor] = useState(initialAnchor); // month-mode pivot

  const WEEKDAYS = t("month.weekdays");

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("mode", mode);
    if (mode === "month") next.set("anchor", toISODate(anchor));
    setSearchParams(next, { replace: true });
  }, [mode, anchor, setSearchParams]);

  // Build the grid the user sees.
  const { gridDays, title } = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    });
    if (mode === "rolling30") {
      const today = new Date();
      // Pad to start on Monday of the week containing today
      const startOffset = weekdayIndexMonFirst(today);
      const start = addDays(today, -startOffset);
      // 30 days from today + leading padding (start..today-1) → total 30 + startOffset
      // Round to full weeks
      const totalDays = 30 + startOffset;
      const ceiled = Math.ceil(totalDays / 7) * 7;
      const days = [];
      for (let i = 0; i < ceiled; i++) {
        const d = addDays(start, i);
        days.push({
          date: d,
          iso: toISODate(d),
          inRange: i >= startOffset && i < startOffset + 30,
          isToday: toISODate(d) === todayISO(),
        });
      }
      return {
        gridDays: days,
        title: t("month.next30"),
      };
    }

    // Full calendar month
    const first = startOfMonth(anchor);
    const last = endOfMonth(anchor);
    const startPad = weekdayIndexMonFirst(first);
    const start = addDays(first, -startPad);
    const totalDays = startPad + last.getDate();
    const ceiled = Math.ceil(totalDays / 7) * 7;
    const days = [];
    for (let i = 0; i < ceiled; i++) {
      const d = addDays(start, i);
      const inRange = d.getMonth() === anchor.getMonth();
      days.push({
        date: d,
        iso: toISODate(d),
        inRange,
        isToday: toISODate(d) === todayISO(),
      });
    }
    return {
      gridDays: days,
      title: monthFormatter.format(first),
    };
  }, [mode, anchor, locale, t]);

  // Group events by date for quick lookup.
  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const ev of getAllEvents()) {
      if (!MONTH_VISIBLE_CATEGORIES.has(ev.category)) continue;
      if (!map.has(ev.date)) map.set(ev.date, []);
      map.get(ev.date).push(ev);
    }
    // Sort each day so higher priority is drawn last (on top).
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.priority || 1) - (b.priority || 1));
    }
    return map;
  }, []);

  const prev = () => {
    if (mode === "rolling30") {
      setMode("month");
      setAnchor(addMonths(new Date(), -1));
    } else {
      setAnchor(addMonths(anchor, -1));
    }
  };
  const next = () => {
    if (mode === "rolling30") {
      setMode("month");
      setAnchor(addMonths(new Date(), 1));
    } else {
      setAnchor(addMonths(anchor, 1));
    }
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-5 py-3 flex items-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100"
            data-testid="month-back"
            aria-label={t("month.backToHome")}
          >
            <ArrowLeft className="h-4 w-4 text-slate-700" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{t("month.overview")}</p>
            <p className="font-display text-base font-semibold text-slate-900 truncate" data-testid="month-title">
              {title}
            </p>
          </div>
          <LanguageSwitcher />
        </div>

        <div className="mx-auto max-w-3xl px-4 sm:px-5 pb-3 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 px-3 border-slate-300"
            onClick={prev}
            data-testid="month-prev"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> {t("month.prev")}
          </Button>
          <div className="flex gap-1 rounded-full bg-slate-100 p-1" data-testid="month-mode-toggle">
            <button
              className={[
                "h-7 px-3 rounded-full text-xs font-medium transition",
                mode === "rolling30" ? "bg-white shadow text-slate-900" : "text-slate-500",
              ].join(" ")}
              onClick={() => setMode("rolling30")}
              data-testid="month-mode-rolling30"
            >
              {t("month.next30")}
            </button>
            <button
              className={[
                "h-7 px-3 rounded-full text-xs font-medium transition",
                mode === "month" ? "bg-white shadow text-slate-900" : "text-slate-500",
              ].join(" ")}
              onClick={() => {
                setMode("month");
                setAnchor((a) => (a.getMonth() === new Date().getMonth() && a.getFullYear() === new Date().getFullYear() ? a : new Date()));
              }}
              data-testid="month-mode-month"
            >
              {t("month.fullMonth")}
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 px-3 border-slate-300"
            onClick={next}
            data-testid="month-next"
          >
            {t("month.next")} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-3xl px-3 sm:px-5 py-4">
        {/* Weekday header */}
        <div className="grid grid-cols-7 mb-1.5 px-1">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="text-center text-[10px] uppercase tracking-[0.18em] text-slate-500"
            >
              {w}
            </div>
          ))}
        </div>

        <div
          className="grid grid-cols-7 gap-1 sm:gap-2"
          data-testid="month-grid"
        >
          {gridDays.map(({ date, iso, inRange, isToday }) => {
            const evs = eventsByDate.get(iso) || [];
            return (
              <button
                key={iso}
                onClick={() => navigate(`/calendar/${iso}`)}
                className={[
                  "btn-lift relative aspect-square rounded-[16px] border bg-card text-left p-1.5 sm:p-2 flex flex-col gap-1",
                  inRange ? "border-border shadow-sm hover:border-slate-300" : "border-transparent opacity-40 hover:opacity-60",
                  isToday ? "ring-2 ring-primary border-primary" : "",
                ].join(" ")}
                data-testid={`month-day-${iso}`}
                data-day-in-range={inRange ? "true" : "false"}
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span
                    className={[
                      "text-[11px] sm:text-sm font-semibold font-mono-tab",
                      isToday ? "text-slate-900" : "text-slate-700",
                    ].join(" ")}
                  >
                    {pad2(date.getDate())}
                  </span>
                  {evs.length > 0 ? (
                    <span className="text-[9px] text-slate-400 font-mono-tab">{evs.length}</span>
                  ) : null}
                </div>
                <DayMiniBars events={evs} />
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-[11px] text-slate-500 text-center px-4">
          {t("month.footnote")}
        </p>
      </main>
    </div>
  );
}

function DayMiniBars({ events }) {
  if (!events.length) return <div className="flex-1" />;
  // Each event is drawn as a thick diagonal line. Multiple events => parallel diagonals
  // distributed across the cell. Higher priority events drawn last (on top, stronger opacity).
  const sorted = [...events].sort((a, b) => (a.priority || 1) - (b.priority || 1));
  const visible = sorted.slice(-5); // keep top 5 by priority order
  const n = visible.length;
  return (
    <div className="flex-1 relative" data-testid="day-bars">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden
      >
        {visible.map((ev, i) => {
          const cat = getCategory(ev.category);
          const span = 35; // % offset range
          const offset = n === 1 ? 0 : -span + (i * (2 * span)) / (n - 1);
          const x1 = Math.max(0, Math.min(100, 5 + offset));
          const y1 = Math.max(0, Math.min(100, 95 + offset));
          const x2 = Math.max(0, Math.min(100, 95 + offset));
          const y2 = Math.max(0, Math.min(100, 5 + offset));
          const strength = 0.6 + Math.min(0.4, (ev.priority || 1) * 0.08);
          return (
            <line
              key={ev.id}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={cat.hex}
              strokeWidth={3.5}
              strokeLinecap="round"
              opacity={strength}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>
    </div>
  );
}
